/**
 * A shared surface for failed write actions.
 *
 * Arul 2026-09-02. The 6-Box CSV import shipped with an `onSuccess` and no
 * `onError`, so a rejected import rendered nothing at all — the button
 * returned to its resting state and the page looked untouched. Carolyn ran
 * two imports believing they had worked; the database showed neither had
 * written a single row. See bugs.md [2026-09-01].
 *
 * Auditing the rest of the page found the same shape on **21 of its 22
 * mutations**: start quarter, lock, unlock, delete quarter, delete
 * assessment, approve, reject, resource upload — every admin action HR
 * touches would fail in silence. The import was not a one-off; it was the
 * one instance somebody happened to notice.
 *
 * Rather than 21 near-identical handlers, mutations report here and this
 * banner renders whatever arrives. Deliberately a module-level event bus
 * rather than context: the mutations are spread across five components
 * inside a 4,700-line file, and this matches the pattern already used by
 * `api/sixBoxImpersonation.ts`.
 *
 * Lives in components/ rather than inside the 6-Box page so other domains
 * can adopt it — the same silent-failure shape almost certainly exists
 * elsewhere.
 */
import React, { useEffect, useState } from 'react';

const EVENT = 'atlas:action-error';

/**
 * Pass as a mutation's `onError`.
 *
 * Typed to `Error` rather than `unknown` on purpose: React Query infers a
 * mutation's error type from its handlers, and an `unknown` parameter widens
 * `TError` to `unknown`, which breaks the several places on this page that
 * already render `someMut.error.message` inline. The runtime check below
 * still copes with a non-Error being thrown.
 */
export function reportActionError(err: Error): void {
  const message = err instanceof Error && err.message
    ? err.message
    : 'That action did not complete. Nothing was changed.';
  window.dispatchEvent(new CustomEvent(EVENT, { detail: message }));
}

/** Clear the banner — e.g. when a later attempt succeeds. */
export function clearActionError(): void {
  window.dispatchEvent(new CustomEvent(EVENT, { detail: null }));
}

/**
 * Renders the most recent failure. Mount once per page.
 *
 * Says "nothing was changed" explicitly, because the failure mode that
 * caused this was a user assuming a silent result meant success.
 */
export function ActionErrorBanner() {
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<string | null>).detail;
      setMessage(detail ?? null);
    };
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, []);

  if (!message) return null;

  return (
    <div
      role="alert"
      className="mb-4 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 flex items-start justify-between gap-3"
    >
      <div className="min-w-0">
        <p className="font-work-sans text-[13px] font-semibold text-rose-900">
          That didn&apos;t work — nothing was changed.
        </p>
        <p className="font-work-sans text-[12px] text-rose-900/85 leading-snug mt-0.5 break-words">
          {message}
        </p>
      </div>
      <button
        type="button"
        onClick={() => setMessage(null)}
        aria-label="Dismiss error"
        className="flex-shrink-0 px-2 py-1 text-xs font-work-sans font-medium text-rose-900 bg-white hover:bg-rose-100 border border-rose-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-400"
      >
        Dismiss
      </button>
    </div>
  );
}

export default ActionErrorBanner;
